import React from 'react';

import { FlexProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PhoneAgentBasicsSection } from 'components/SmartAssistantSections/PhoneAgentBasicsSection/PhoneAgentBasicsSection';
import { usePhoneSmartAssistantForms } from 'hooks/SmartAssistantForms/use-phone-smart-assistant-forms';
import { useMultiUpdateNotificationHandling } from 'hooks/use-multi-update-notification-handling';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface PhoneAgentBasicsManagementSectionProps extends FlexProps {
  smartAssistant: SmartAssistant;
}

export function PhoneAgentBasicsManagementSection({ smartAssistant }: PhoneAgentBasicsManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { phoneAgentBasicsForm } = usePhoneSmartAssistantForms(smartAssistant);
  const { showUpdateNotification, resetNotificationStatusTracking } = useMultiUpdateNotificationHandling({
    feedbackKey: 'UPDATE_PHONE_AGENT_BASICS'
  });

  const saveSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      phoneAgentBasicsForm.resetDirty();
      showUpdateNotification(true);
    },
    onError: () => showUpdateNotification(false)
  });

  const updateIntegrationsMutation = useMutation({
    mutationFn: IntegrationManagerService.updateSmartAssistantIntegrations,
    onSuccess: () => {
      phoneAgentBasicsForm.resetDirty();
      showUpdateNotification(true);
    },
    onError: () => showUpdateNotification(false)
  });

  const onSave = async () => {
    resetNotificationStatusTracking();
    const validation = phoneAgentBasicsForm.validate();
    if (validation.hasErrors) return;

    try {
      const preSmartAssistantUpdatePromises = [];
      const shouldUpdateVoiceIntegration =
        phoneAgentBasicsForm.values.transcriptionConfiguration !==
          smartAssistant.voiceIntegration?.transcriptionConfiguration ||
        phoneAgentBasicsForm.values.voiceConfiguration !== smartAssistant.voiceIntegration?.voiceConfiguration;

      if (shouldUpdateVoiceIntegration) {
        preSmartAssistantUpdatePromises.push(
          updateIntegrationsMutation.mutateAsync({
            smartAssistantId: smartAssistant._id,
            voiceIntegration: {
              transcriptionConfiguration: phoneAgentBasicsForm.values.transcriptionConfiguration,
              voiceConfiguration: phoneAgentBasicsForm.values.voiceConfiguration,
              // The configuration below are not updated by the user in this section, we keep the existing values
              telephonyConfiguration: smartAssistant.voiceIntegration?.telephonyConfiguration,
              deployConfiguration: smartAssistant.voiceIntegration?.deployConfiguration
            }
          })
        );
      }

      await Promise.all(preSmartAssistantUpdatePromises);

      await saveSmartAssistantMutation.mutateAsync({
        id: smartAssistant._id,
        name: phoneAgentBasicsForm.values.name,
        welcomeMessage: phoneAgentBasicsForm.values.welcomeMessage
      });
    } catch {
      showUpdateNotification(false);
    }
  };

  const onCancel = () => {
    phoneAgentBasicsForm.reset();
    resetNotificationStatusTracking();
  };

  const isLoading = saveSmartAssistantMutation.isLoading || updateIntegrationsMutation.isLoading;

  const hasChanges = phoneAgentBasicsForm.isDirty();

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.PHONE_AGENT_BASICS_SECTION.TITLE')}
      hasChanges={hasChanges}
      loading={isLoading}
      onSave={onSave}
      onCancel={onCancel}>
      <PhoneAgentBasicsSection form={phoneAgentBasicsForm} smartAssistant={smartAssistant} />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
